// Compact currency switcher — matches LanguageSwitcher styling. Auto-detects
// from IP on first mount via CurrencyContext, can be manually overridden.
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";

const CCY_LABELS = {
  EUR: "EUR €", USD: "USD $", GBP: "GBP £", CAD: "CAD $", CHF: "CHF",
  AUD: "AUD $", SEK: "SEK kr", NOK: "NOK kr", DKK: "DKK kr",
};

export const CurrencySwitcher = ({ compact = false, testIdPrefix = "ccy" }) => {
  const { currency, setCurrency, supported, autoDetected } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const label = CCY_LABELS[currency] || currency;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Devise : ${label}${autoDetected ? " (auto-détectée)" : ""}`}
        title={`Devise : ${label}${autoDetected ? " — détectée depuis votre localisation" : ""}`}
        className={`inline-flex items-center gap-1.5 rounded-[5px] border border-[color:var(--gv-line)] text-[color:var(--gv-ink)] hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] transition-colors ${compact ? "px-2.5 py-2 text-[13px]" : "px-3 py-2.5 text-[13.5px]"}`}
        data-testid={`${testIdPrefix}-switcher-toggle`}
      >
        <span className="font-medium">{currency}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 w-40 bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-[0_24px_60px_-20px_rgba(18,24,32,0.25)] overflow-hidden z-[60]"
          data-testid={`${testIdPrefix}-switcher-menu`}
        >
          {supported.map((code) => (
            <li key={code} role="option" aria-selected={code === currency}>
              <button
                type="button"
                onClick={() => { setCurrency(code); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-left text-[13.5px] hover:bg-[color:var(--gv-mist)] ${code === currency ? "text-[color:var(--gv-pink)] font-medium" : "text-[color:var(--gv-ink)]"}`}
                data-testid={`${testIdPrefix}-switcher-${code}`}
              >
                <span>{CCY_LABELS[code] || code}</span>
                {code === currency && <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)]/70">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CurrencySwitcher;
